import * as THREE from 'three';
import { getPortalCellCenter } from './portalPair';
import { getLabelContext } from './labelTextures';

export const createPortalPairScene = (scene, surfaceY) => {
  const group = new THREE.Group();
  group.visible = false;
  scene.add(group);
  const gates = [0x6dfff1, 0xb49aff].map((color, index) => {
    const gate = new THREE.Group();
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      toneMapped: false,
    });
    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(1.1, 0.035, 8, 64),
      material
    );
    const inner = new THREE.Mesh(
      new THREE.TorusGeometry(0.94, 0.018, 6, 48, Math.PI * 1.65),
      material.clone()
    );
    rim.rotation.x = inner.rotation.x = Math.PI / 2;
    const pool = new THREE.Mesh(
      new THREE.CircleGeometry(0.88, 40),
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.12,
        depthWrite: false,
        side: THREE.DoubleSide,
      })
    );
    pool.rotation.x = -Math.PI / 2;
    pool.position.y = -0.01;
    gate.add(rim, inner, pool);
    const canvas = document.createElement('canvas');
    canvas.width = 160;
    canvas.height = 64;
    const context = getLabelContext(canvas);
    if (context) {
      context.font = 'bold 38px monospace';
      context.textAlign = 'center';
      context.fillStyle = index ? '#d1bfff' : '#9efff4';
      context.fillText(index ? 'B' : 'A', 80, 44);
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      const label = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
          depthWrite: false,
          toneMapped: false,
        })
      );
      label.position.set(0, 0.2, 1.05);
      label.scale.set(0.9, 0.36, 1);
      gate.add(label);
    }
    group.add(gate);
    return { gate, rim, inner, pool };
  });
  const positions = new Float32Array(33 * 3);
  const colors = new Float32Array(33 * 3);
  for (let i = 0; i <= 32; i++) {
    const color = new THREE.Color(0x6dfff1).lerp(
      new THREE.Color(0xb49aff),
      i / 32
    );
    color.toArray(colors, i * 3);
  }
  const pathGeometry = new THREE.BufferGeometry();
  pathGeometry.setAttribute(
    'position',
    new THREE.BufferAttribute(positions, 3)
  );
  pathGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  const path = new THREE.Line(
    pathGeometry,
    new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      toneMapped: false,
    })
  );
  group.add(path);

  const transit = (fromIndex, toIndex) => {
    const from = getPortalCellCenter(fromIndex);
    const to = getPortalCellCenter(toIndex);
    for (let i = 0; i <= 32; i++) {
      const t = i / 32;
      positions[i * 3] = THREE.MathUtils.lerp(from.x, to.x, t);
      positions[i * 3 + 1] = surfaceY + 0.15 + Math.sin(t * Math.PI) * 2;
      positions[i * 3 + 2] = THREE.MathUtils.lerp(from.z, to.z, t);
    }
    pathGeometry.attributes.position.needsUpdate = true;
    pathGeometry.computeBoundingSphere();
  };
  const update = (pair, flash, elapsed, reduceMotion) => {
    group.visible =
      Number.isInteger(pair?.entryIndex) &&
      Number.isInteger(pair?.exitIndex);
    if (!group.visible) return;
    gates.forEach(({ gate, rim, inner, pool }, index) => {
      const cell = index ? pair.exitIndex : pair.entryIndex;
      gate.visible = Number.isInteger(cell);
      if (!gate.visible) return;
      const center = getPortalCellCenter(cell);
      gate.position.set(center.x, surfaceY + 0.075, center.z);
      gate.scale.setScalar(reduceMotion ? 1 : 1 + flash * 0.2);
      inner.rotation.z = reduceMotion ? 0 : elapsed * (index ? -0.9 : 0.9);
      rim.material.opacity = 0.7 + flash * 0.3;
      pool.material.opacity = 0.12 + flash * 0.28;
    });
    path.visible = flash > 0 && !reduceMotion;
    path.material.opacity = flash * 0.9;
  };
  return { group, transit, update };
};
